import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Search,
  Filter,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Users,
  FileText,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  Bell,
  Zap,
  Eye,
  Phone,
  Mail,
  MoreVertical,
  RefreshCw,
  LayoutGrid,
  List,
  Kanban,
  ArrowUpRight,
  Star,
  Flag,
  Target,
  TrendingUp,
  Crown,
  ListChecks,
  X,
  UserPlus,
  Lock,
  Trash2,
  Wrench
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Step configuration with explicit Tailwind classes
const STEPS = [
  { id: 1, name: "Activación", short: "ACT", bgColor: "bg-emerald-50", borderColor: "border-emerald-200", textColor: "text-emerald-700", dotBg: "bg-emerald-200" },
  { id: 2, name: "Análisis", short: "ANA", bgColor: "bg-blue-50", borderColor: "border-blue-200", textColor: "text-blue-700", dotBg: "bg-blue-200" },
  { id: 3, name: "Documentos", short: "DOC", bgColor: "bg-purple-50", borderColor: "border-purple-200", textColor: "text-purple-700", dotBg: "bg-purple-200" },
  { id: 4, name: "DS-160", short: "DS", bgColor: "bg-indigo-50", borderColor: "border-indigo-200", textColor: "text-indigo-700", dotBg: "bg-indigo-200" },
  { id: 5, name: "Pago/Cita", short: "PAG", bgColor: "bg-amber-50", borderColor: "border-amber-200", textColor: "text-amber-700", dotBg: "bg-amber-200" },
  { id: 6, name: "Optimización", short: "OPT", bgColor: "bg-cyan-50", borderColor: "border-cyan-200", textColor: "text-cyan-700", dotBg: "bg-cyan-200" },
  { id: 7, name: "Preparación", short: "PREP", bgColor: "bg-orange-50", borderColor: "border-orange-200", textColor: "text-orange-700", dotBg: "bg-orange-200" },
  { id: 8, name: "Entrevista", short: "ENT", bgColor: "bg-rose-50", borderColor: "border-rose-200", textColor: "text-rose-700", dotBg: "bg-rose-200" },
  { id: 9, name: "Resultado", short: "RES", bgColor: "bg-yellow-50", borderColor: "border-yellow-200", textColor: "text-yellow-700", dotBg: "bg-yellow-200" }
];

const riskColors = {
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  high: "bg-red-100 text-red-700 border-red-200",
  pending: "bg-gray-100 text-gray-600 border-gray-200"
};

// Case Card Component
const CaseCard = ({ caseData, onQuickAction, onDeleteCase, isSelected, onSelect, deletingId }) => {
  const [showActions, setShowActions] = useState(false);
  const navigate = useNavigate();
  
  const isDeleting = deletingId === caseData.id;
  
  const daysUntilInterview = useMemo(() => {
    if (!caseData.interview_date) return null;
    const diff = new Date(caseData.interview_date) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }, [caseData.interview_date]);

  const isUrgent = daysUntilInterview !== null && daysUntilInterview <= 7 && daysUntilInterview > 0;
  const isPastDue = daysUntilInterview !== null && daysUntilInterview < 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`bg-white rounded-xl border-2 transition-all cursor-pointer group ${
        isSelected 
          ? "border-gold-500 shadow-lg" 
          : isUrgent 
          ? "border-red-300 shadow-md" 
          : "border-navy-100 hover:border-navy-200 hover:shadow-md"
      }`}
      onClick={() => navigate(`/admin/cases/${caseData.id}`)}
    >
      {/* Urgency Banner */}
      {isUrgent && (
        <div className="bg-red-500 text-white text-xs font-medium px-3 py-1 rounded-t-lg flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Entrevista en {daysUntilInterview} día{daysUntilInterview !== 1 ? 's' : ''}
        </div>
      )}
      
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
              caseData.user?.plan_type === "advanced" 
                ? "bg-purple-100 text-purple-700" 
                : "bg-navy-100 text-navy-600"
            }`}>
              {caseData.user?.name?.[0] || "?"}
            </div>
            <div>
              <p className="font-medium text-navy-900 text-sm flex items-center gap-1">
                {caseData.user?.name || "Sin nombre"}
                {caseData.user?.plan_type === "advanced" && (
                  <Crown className="w-3 h-3 text-purple-500" />
                )}
              </p>
              <p className="text-xs text-navy-500">{caseData.visa_type}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${riskColors[caseData.risk_level] || riskColors.pending}`}>
              {caseData.risk_level === "low" ? "Bajo" : 
               caseData.risk_level === "medium" ? "Medio" : 
               caseData.risk_level === "high" ? "Alto" : "—"}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-navy-500">Etapa {caseData.current_step}/9</span>
            <span className="font-medium text-navy-700">{caseData.progress_percentage}%</span>
          </div>
          <div className="h-1.5 bg-navy-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-gold-500 to-gold-400 rounded-full transition-all"
              style={{ width: `${caseData.progress_percentage}%` }}
            />
          </div>
        </div>

        {/* Quick Info */}
        <div className="flex items-center gap-3 text-xs text-navy-500">
          {caseData.probability_score && (
            <span className="flex items-center gap-1">
              <Target className="w-3 h-3" />
              {caseData.probability_score}%
            </span>
          )}
          {caseData.interview_date && (
            <span className={`flex items-center gap-1 ${isUrgent ? "text-red-600 font-medium" : ""}`}>
              <Calendar className="w-3 h-3" />
              {new Date(caseData.interview_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
            </span>
          )}
          {caseData.monitoring_active && (
            <span className="flex items-center gap-1 text-cyan-600">
              <Zap className="w-3 h-3" />
              OPT
            </span>
          )}
        </div>

        {/* Quick Actions on Hover */}
        <div className="mt-3 pt-3 border-t border-navy-100 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1">
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction(caseData.id, "advance");
              }}
            >
              <ArrowUpRight className="w-3 h-3 mr-1" />
              Avanzar
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`mailto:${caseData.user?.email}`, '_blank');
              }}
            >
              <Mail className="w-3 h-3" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteCase(caseData.id, caseData.user?.name || 'Sin nombre');
              }}
              disabled={isDeleting}
              data-testid={`delete-case-btn-${caseData.id}`}
            >
              {isDeleting ? (
                <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3" />
              )}
            </Button>
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 px-2 text-xs text-gold-600"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/cases/${caseData.id}`);
            }}
          >
            Ver más
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Today's Panel Component
const TodayPanel = ({ cases, standardPendingCount, chatCount, onRefresh }) => {
  const today = new Date();
  
  const todayInterviews = cases.filter(c => {
    if (!c.interview_date) return false;
    const intDate = new Date(c.interview_date);
    return intDate.toDateString() === today.toDateString();
  });

  const urgentCases = cases.filter(c => {
    if (!c.interview_date) return false;
    const diff = new Date(c.interview_date) - today;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 && days <= 7;
  });

  const highRiskActive = cases.filter(c => c.risk_level === "high" && c.status === "active");

  return (
    <div className="card-elevated p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold text-navy-900">
          📋 Hoy - {today.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
        </h2>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className={`p-3 rounded-xl ${todayInterviews.length > 0 ? "bg-red-50 border border-red-200" : "bg-navy-50"}`}>
          <p className="text-xs text-navy-500">Entrevistas Hoy</p>
          <p className={`text-2xl font-bold ${todayInterviews.length > 0 ? "text-red-600" : "text-navy-400"}`}>
            {todayInterviews.length}
          </p>
        </div>
        
        <div className={`p-3 rounded-xl ${urgentCases.length > 0 ? "bg-amber-50 border border-amber-200" : "bg-navy-50"}`}>
          <p className="text-xs text-navy-500">Próx. 7 días</p>
          <p className={`text-2xl font-bold ${urgentCases.length > 0 ? "text-amber-600" : "text-navy-400"}`}>
            {urgentCases.length}
          </p>
        </div>
        
        <div className={`p-3 rounded-xl ${highRiskActive.length > 0 ? "bg-red-50 border border-red-200" : "bg-navy-50"}`}>
          <p className="text-xs text-navy-500">Alto Riesgo</p>
          <p className={`text-2xl font-bold ${highRiskActive.length > 0 ? "text-red-600" : "text-navy-400"}`}>
            {highRiskActive.length}
          </p>
        </div>
        
        <div className={`p-3 rounded-xl ${chatCount > 0 ? "bg-purple-50 border border-purple-200" : "bg-navy-50"}`}>
          <p className="text-xs text-navy-500">Chats Pendientes</p>
          <p className={`text-2xl font-bold ${chatCount > 0 ? "text-purple-600" : "text-navy-400"}`}>
            {chatCount}
          </p>
        </div>
        
        <div className={`p-3 rounded-xl ${standardPendingCount > 0 ? "bg-blue-50 border border-blue-200" : "bg-navy-50"}`}>
          <p className="text-xs text-navy-500">Standard Listos</p>
          <p className={`text-2xl font-bold ${standardPendingCount > 0 ? "text-blue-600" : "text-navy-400"}`}>
            {standardPendingCount}
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Command Center Component
const AdminCommandCenter = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingCaseId, setDeletingCaseId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRisk, setFilterRisk] = useState("all");
  const [filterStep, setFilterStep] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [viewMode, setViewMode] = useState("kanban"); // kanban, list, grid
  const [standardPendingCount, setStandardPendingCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [selectedCases, setSelectedCases] = useState([]);
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [casesRes, standardRes, chatRes] = await Promise.all([
        axios.get(`${API}/admin/cases`),
        axios.get(`${API}/autoservicio/admin/queue`),
        axios.get(`${API}/admin/conversations`)
      ]);
      setCases(casesRes.data);
      setStandardPendingCount(standardRes.data.total || 0);
      setChatCount(chatRes.data.filter(c => c.unread_by_admin).length);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (caseId, action) => {
    try {
      if (action === "advance") {
        await axios.post(`${API}/admin/cases/${caseId}/advance-step`);
        toast.success("Caso avanzado al siguiente paso");
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ejecutar acción");
    }
  };

  const handleDeleteCase = async (caseId, clientName) => {
    // Confirmation dialog
    if (!window.confirm(`¿Estás seguro de que quieres ELIMINAR el caso de "${clientName}"?\n\nEsta acción eliminará permanentemente:\n- El caso y todos sus pasos\n- Todos los documentos asociados\n- Todas las notas del caso\n- Todas las notificaciones relacionadas\n\n⚠️ Esta acción NO se puede deshacer.`)) {
      return;
    }

    setDeletingCaseId(caseId);
    try {
      await axios.delete(`${API}/admin/cases/${caseId}`);
      toast.success(`Caso de "${clientName}" eliminado exitosamente`);
      // Refetch data to ensure consistency (avoid UI freeze from state mutation)
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el caso");
    } finally {
      setDeletingCaseId(null);
    }
  };

  // Filter cases
  const filteredCases = useMemo(() => {
    return cases.filter(c => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchName = c.user?.name?.toLowerCase().includes(query);
        const matchEmail = c.user?.email?.toLowerCase().includes(query);
        const matchId = c.id?.toLowerCase().includes(query);
        if (!matchName && !matchEmail && !matchId) return false;
      }
      
      // Risk filter
      if (filterRisk !== "all" && c.risk_level !== filterRisk) return false;
      
      // Step filter
      if (filterStep !== "all" && c.current_step !== parseInt(filterStep)) return false;
      
      // Plan filter
      if (filterPlan !== "all") {
        if (filterPlan === "advanced" && c.user?.plan_type !== "advanced") return false;
        if (filterPlan === "standard" && c.user?.plan_type === "advanced") return false;
      }
      
      return true;
    });
  }, [cases, searchQuery, filterRisk, filterStep, filterPlan]);

  // Group cases by step for Kanban
  const casesByStep = useMemo(() => {
    const grouped = {};
    STEPS.forEach(step => {
      grouped[step.id] = filteredCases.filter(c => c.current_step === step.id);
    });
    return grouped;
  }, [filteredCases]);

  const handleRepairAllCases = async () => {
    if (!window.confirm("¿Estás seguro de que deseas reparar los pasos de todos los casos? Esto creará los 6 pasos del 'Camino al Éxito' para todos los casos que no los tengan.")) {
      return;
    }
    
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/admin/repair-all-cases-steps`);
      toast.success(`✅ ${res.data.message}`);
      console.log("Repair result:", res.data);
      // Refresh cases after repair
      fetchCases();
    } catch (error) {
      console.error("Repair error:", error);
      toast.error("Error al reparar los casos: " + (error.response?.data?.detail || error.message));
    } finally {
      setRepairing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900 truncate">
              Centro de Comando
            </h1>
            <p className="text-navy-500 text-sm sm:text-base">
              {cases.length} casos totales • {filteredCases.length} visibles
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Repair Button */}
            <Button 
              onClick={handleRepairAllCases}
              disabled={repairing}
              variant="outline"
              size="sm"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="repair-cases-btn"
            >
              <Wrench className="w-4 h-4 mr-1" />
              {repairing ? "Reparando..." : "Reparar Pasos"}
            </Button>
            {/* Quick Links */}
            <Link to="/admin/chat">
              <Button variant="outline" size="sm" className="relative">
                <MessageCircle className="w-4 h-4 mr-1" />
                Chat
                {chatCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
                    {chatCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/admin/standard-queue">
              <Button variant="outline" size="sm" className="relative">
                <ListChecks className="w-4 h-4 mr-1" />
                Standard
                {standardPendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                    {standardPendingCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/admin/analytics">
              <Button variant="outline" size="sm">
                <TrendingUp className="w-4 h-4 mr-1" />
                Analytics
              </Button>
            </Link>
          </div>
        </div>

        {/* Today's Panel */}
        <TodayPanel 
          cases={cases} 
          standardPendingCount={standardPendingCount} 
          chatCount={chatCount}
          onRefresh={fetchData}
        />

        {/* Search and Filters */}
        <div className="card-elevated p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                placeholder="Buscar por nombre, email o ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="command-center-search"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                  data-testid="clear-search-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Filters */}
            <div className="flex items-center gap-2">
              <Select value={filterRisk} onValueChange={setFilterRisk}>
                <SelectTrigger className="w-32" data-testid="filter-risk-select">
                  <SelectValue placeholder="Riesgo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo Riesgo</SelectItem>
                  <SelectItem value="high">🔴 Alto</SelectItem>
                  <SelectItem value="medium">🟡 Medio</SelectItem>
                  <SelectItem value="low">🟢 Bajo</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterStep} onValueChange={setFilterStep}>
                <SelectTrigger className="w-36" data-testid="filter-step-select">
                  <SelectValue placeholder="Etapa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las Etapas</SelectItem>
                  {STEPS.map(step => (
                    <SelectItem key={step.id} value={step.id.toString()}>
                      {step.id}. {step.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-32" data-testid="filter-plan-select">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="vip">👑 VIP</SelectItem>
                  <SelectItem value="advanced">⭐ Premium</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode */}
              <div className="flex items-center border rounded-lg p-1 bg-navy-50" data-testid="view-mode-toggle">
                <button
                  onClick={() => setViewMode("kanban")}
                  className={`p-1.5 rounded ${viewMode === "kanban" ? "bg-white shadow-sm" : "text-navy-400"}`}
                  data-testid="view-kanban-btn"
                >
                  <Kanban className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-1.5 rounded ${viewMode === "grid" ? "bg-white shadow-sm" : "text-navy-400"}`}
                  data-testid="view-grid-btn"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-1.5 rounded ${viewMode === "list" ? "bg-white shadow-sm" : "text-navy-400"}`}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Cases Display */}
        {viewMode === "kanban" ? (
          /* Kanban View */
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {STEPS.map(step => (
                <div key={step.id} className="w-72 flex-shrink-0">
                  {/* Column Header */}
                  <div className={`p-3 rounded-t-xl ${step.bgColor} border-b-2 ${step.borderColor}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full ${step.dotBg} ${step.textColor} text-xs font-bold flex items-center justify-center`}>
                          {step.id}
                        </span>
                        <span className="font-medium text-navy-900">{step.name}</span>
                      </div>
                      <span className="text-sm font-bold text-navy-600">
                        {casesByStep[step.id]?.length || 0}
                      </span>
                    </div>
                  </div>
                  
                  {/* Column Content */}
                  <div className="bg-navy-50/50 rounded-b-xl p-2 min-h-[400px] space-y-2">
                    <AnimatePresence>
                      {casesByStep[step.id]?.map(caseData => (
                        <CaseCard
                          key={caseData.id}
                          caseData={caseData}
                          onQuickAction={handleQuickAction}
                          onDeleteCase={handleDeleteCase}
                          deletingId={deletingCaseId}
                          isSelected={selectedCases.includes(caseData.id)}
                          onSelect={(id) => {
                            setSelectedCases(prev => 
                              prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                            );
                          }}
                        />
                      ))}
                    </AnimatePresence>
                    {casesByStep[step.id]?.length === 0 && (
                      <div className="text-center py-8 text-navy-400 text-sm">
                        Sin casos
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : viewMode === "grid" ? (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <AnimatePresence>
              {filteredCases.map(caseData => (
                <CaseCard
                  key={caseData.id}
                  caseData={caseData}
                  onQuickAction={handleQuickAction}
                  onDeleteCase={handleDeleteCase}
                  deletingId={deletingCaseId}
                  isSelected={selectedCases.includes(caseData.id)}
                  onSelect={(id) => {
                    setSelectedCases(prev => 
                      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                    );
                  }}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          /* List View */
          <div className="card-elevated overflow-hidden">
            <table className="w-full">
              <thead className="bg-navy-50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-navy-600">Cliente</th>
                  <th className="text-left p-4 text-sm font-medium text-navy-600">Etapa</th>
                  <th className="text-left p-4 text-sm font-medium text-navy-600">Riesgo</th>
                  <th className="text-left p-4 text-sm font-medium text-navy-600">Prob.</th>
                  <th className="text-left p-4 text-sm font-medium text-navy-600">Entrevista</th>
                  <th className="text-left p-4 text-sm font-medium text-navy-600">Plan</th>
                  <th className="text-right p-4 text-sm font-medium text-navy-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {filteredCases.map(caseData => (
                  <tr 
                    key={caseData.id} 
                    className="hover:bg-navy-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/cases/${caseData.id}`)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          caseData.user?.plan_type === "advanced" 
                            ? "bg-purple-100 text-purple-700" 
                            : "bg-navy-100 text-navy-600"
                        }`}>
                          {caseData.user?.name?.[0] || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-navy-900">{caseData.user?.name}</p>
                          <p className="text-xs text-navy-500">{caseData.user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">
                        {caseData.current_step}. {STEPS.find(s => s.id === caseData.current_step)?.name}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${riskColors[caseData.risk_level] || riskColors.pending}`}>
                        {caseData.risk_level === "low" ? "Bajo" : 
                         caseData.risk_level === "medium" ? "Medio" : 
                         caseData.risk_level === "high" ? "Alto" : "—"}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm font-medium">{caseData.probability_score || "—"}%</span>
                    </td>
                    <td className="p-4">
                      {caseData.interview_date ? (
                        <span className="text-sm">
                          {new Date(caseData.interview_date).toLocaleDateString('es-ES')}
                        </span>
                      ) : (
                        <span className="text-navy-400">—</span>
                      )}
                    </td>
                    <td className="p-4">
                      {caseData.user?.plan_type === "advanced" ? (
                        <span className="flex items-center gap-1 text-purple-600 text-sm">
                          <Crown className="w-3 h-3" />
                          Premium
                        </span>
                      ) : (
                        <span className="text-sm text-navy-500">Standard</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickAction(caseData.id, "advance");
                          }}
                        >
                          Avanzar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCase(caseData.id, caseData.user?.name || 'Sin nombre');
                          }}
                          disabled={deletingCaseId === caseData.id}
                          data-testid={`delete-case-list-btn-${caseData.id}`}
                        >
                          {deletingCaseId === caseData.id ? (
                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredCases.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-navy-300 mx-auto mb-3" />
            <p className="text-navy-500">No se encontraron casos con los filtros actuales</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3"
              onClick={() => {
                setSearchQuery("");
                setFilterRisk("all");
                setFilterStep("all");
                setFilterPlan("all");
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminCommandCenter;
